/**
 * Path segment constants - Single source of truth for all path segments
 * Used by both API endpoints and frontend routes
 * If a path changes (e.g., /interaction -> /inter), update it here only
 */
export const PATH_SEGMENTS = {
  // Auth
  AUTH: 'auth',
  LOGIN: 'login',
  LOGOUT: 'logout',
  REFRESH: 'refresh',
  
  // Interactions
  INTERACTION: 'interaction',        // Base segment (API uses singular)
  INTERACTIONS: 'interactions',      // Frontend routes use plural
  
  // Moments
  MOMENT: 'moment',
  MOMENTS: 'moments',
  MOMENT_VIEW: 'momentview',
  MOMENT_EDIT: 'edit',
  MOMENT_NEW: 'new',
  CATEGORY: 'category',
  
  // Utterances
  UTTERANCE: 'utterance',
  UTTERANCES: 'utterances',

  // Metadata
  METADATA: 'metadata',
  
  // Evaluation Forms
  EVALFORM: 'evalform',
  SECTION: 'section',
  QUESTION: 'question',
  OPTIONS: 'options',
  
  // Other resources
  AUDIO: 'audio',
  TRANSCRIPT: 'transcript',
  EVALUATION: 'evaluation',
  ADHERENCE: 'adherence',
  USER: 'users',
  SUMMARY: 'summary',
  KPI: 'kpi',
  EXECUTION: 'execution',
  TA: 'ta',

  /** Live Assist: UI routes under `/live-assist/...` and API `/live-assist/...` */
  LIVE_ASSIST: 'live-assist',
  LIVE_ASSIST_LIVE: 'live',
  LIVE_ASSIST_REPLAY: 'replay',

  /** Public live demos (no login): UI `/live-demo/...`, API `/public/demos/...` */
  LIVE_DEMO: 'live-demo',
  LIVE_DEMO_PLAY: 'play',
  PUBLIC_DEMOS: 'public/demos',
  
  // Additional resources (for API endpoints)
  LANGUAGE: 'language',
  CHANNEL: 'channel',
  OCCURRENCE: 'occurrence',
  SPEAKER: 'speaker',
  SENTIMENT: 'sentiment',
  SEARCH_METHOD: 'searchmethod',
  RESOURCE_STATE: 'resource_state',
  BOOLEAN: 'boolean',
  QUESTION_TYPE: 'question_type',
  OPTION_TYPE: 'option_type',
  MOMENT_OPERATION: 'moment_operation',
  SPEECHTEXT: 'speechtext',
  REMARK: 'remark',
  
  // Test
  TEST_AUDIO: 'test-audio',
} as const;

/**
 * Helper function to create frontend route paths
 * Prepends '/' to path segments
 * Usage: route(PATH_SEGMENTS.INTERACTIONS) or route(PATH_SEGMENTS.INTERACTION, id)
 */
export const route = (...segments: (string | number)[]): string => {
  return '/' + segments.join('/');
};

/**
 * Helper function factory to create API endpoint paths
 * Returns a function that prepends baseUrl and '/' to path segments
 * Usage: const api = apiEndpoint(config.api.baseUrl); api(PATH_SEGMENTS.INTERACTION, id)
 * 
 * Note: The actual `api` function is exported from '../services/api'
 * Both `route()` and `api()` use the same PATH_SEGMENTS for consistency
 * 
 * Example for navigation: route(PATH_SEGMENTS.AUTH, PATH_SEGMENTS.LOGIN) -> /auth/login
 * Example for API: api(PATH_SEGMENTS.AUTH, PATH_SEGMENTS.LOGIN) -> http://localhost:8000/auth/login

 */
export const apiEndpoint = (baseUrl: string) => (...segments: (string | number)[]): string => {
  return baseUrl + '/' + segments.join('/');
};

